import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api';
import { Icon } from '../../ui/Icon';
import { addComboItem, choiceRule, comboCandidates, move, newGroup, newOption, toggleDefault, type GroupDraft, type OptionDraft, type ProductDraft } from '../lib/draft.js';
import { priceLabel } from '../lib/list.js';
import { catalogKeys } from '../keys';
import { Field, ListError, invalid, type Errors, type SetField, type Update } from './fields';

const SECTIONS = ['Inihaw', 'Meals', 'Sides', 'Drinks', 'Desserts'];

/** Grill: prep minutes, menu section, modifier groups (choices) and the combo builder. */
export function GrillSection({ d, errors, set, update, shopId, selfId }: { d: ProductDraft; errors: Errors; set: SetField; update: Update; shopId: string; selfId: string | null }) {
  const setGroup = (key: string, fn: (g: GroupDraft) => GroupDraft) => update((x) => ({ ...x, groups: x.groups.map((g) => (g.key === key ? fn(g) : g)) }), 'groups');
  const setOption = (gk: string, ok: string, patch: Partial<OptionDraft>) =>
    setGroup(gk, (g) => ({ ...g, options: g.options.map((o) => (o.key === ok ? { ...o, ...patch } : o)) }));

  return (
    <section className="card panel" aria-labelledby="sec-grill">
      <h2 id="sec-grill">Grill</h2>
      <div className="cat-grid2">
        <Field name="prepMinutes" label="Prep time" error={errors.prepMinutes} help="How long the kitchen needs. The ticket board counts down from it.">
          <div className="input-affix" data-invalid={errors.prepMinutes ? 'true' : undefined}>
            <input {...invalid('prepMinutes', errors.prepMinutes)} inputMode="numeric" value={d.prepMinutes} onChange={(e) => set('prepMinutes', e.target.value)} />
            <span className="muted">min</span>
          </div>
        </Field>
        <Field name="menuSection" label="Menu section" error={errors.menuSection} help="Groups the menu in the app, like Inihaw or Drinks.">
          <input {...invalid('menuSection', errors.menuSection)} className="input" list="cat-sections" maxLength={40} value={d.menuSection} onChange={(e) => set('menuSection', e.target.value)} />
          <datalist id="cat-sections">
            {SECTIONS.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
        </Field>
      </div>

      <fieldset className="cat-fieldset">
        <legend className="t-label">Choices</legend>
        <p className="t-caption muted">Groups the customer picks from, like a sauce (required, pick 1) or add-ons (optional). Prices add to the item.</p>
        <ol className="stack s12">
          {d.groups.map((g, gi) => (
            <li key={g.key} className="cat-group">
              <div className="row wrap">
                <div className="field grow">
                  <label htmlFor={`grp-${g.key}`}>Group {gi + 1}</label>
                  <input id={`grp-${g.key}`} className="input" placeholder="Sauce" maxLength={40} value={g.name} onChange={(e) => setGroup(g.key, (x) => ({ ...x, name: e.target.value }))} />
                </div>
                <div className="field cat-num">
                  <label htmlFor={`grp-${g.key}-min`}>Min</label>
                  <input id={`grp-${g.key}-min`} className="input" inputMode="numeric" value={g.min} onChange={(e) => setGroup(g.key, (x) => ({ ...x, min: e.target.value }))} />
                </div>
                <div className="field cat-num">
                  <label htmlFor={`grp-${g.key}-max`}>Max</label>
                  <input id={`grp-${g.key}-max`} className="input" inputMode="numeric" value={g.max} onChange={(e) => setGroup(g.key, (x) => ({ ...x, max: e.target.value }))} />
                </div>
                <span className="row cat-tools">
                  <button type="button" className="btn btn-quiet icon-btn cat-up" aria-label={`Move ${g.name || `group ${gi + 1}`} up`} disabled={gi === 0} onClick={() => update((x) => ({ ...x, groups: move(x.groups, gi, -1) }))}>
                    <Icon name="arrow" />
                  </button>
                  <button type="button" className="btn btn-quiet icon-btn cat-down" aria-label={`Move ${g.name || `group ${gi + 1}`} down`} disabled={gi === d.groups.length - 1} onClick={() => update((x) => ({ ...x, groups: move(x.groups, gi, 1) }))}>
                    <Icon name="arrow" />
                  </button>
                  <button type="button" className="btn btn-quiet icon-btn" aria-label={`Remove ${g.name || `group ${gi + 1}`}`} onClick={() => update((x) => ({ ...x, groups: x.groups.filter((y) => y.key !== g.key) }), 'groups')}>
                    <Icon name="close" />
                  </button>
                </span>
              </div>
              <span className="t-monocap muted">{choiceRule(g)}</span>
              <ul className="stack s8">
                {g.options.map((o, oi) => (
                  <li key={o.key} className="cat-option">
                    <input className="input" aria-label={`${g.name || `Group ${gi + 1}`} option ${oi + 1}`} placeholder="Sweet" maxLength={40} value={o.label} onChange={(e) => setOption(g.key, o.key, { label: e.target.value })} />
                    <div className="input-affix">
                      <span aria-hidden="true">+₱</span>
                      <input aria-label={`${o.label || `Option ${oi + 1}`} extra price in pesos`} inputMode="decimal" placeholder="0" value={o.price} onChange={(e) => setOption(g.key, o.key, { price: e.target.value })} />
                    </div>
                    <label className="check-label">
                      <input type="checkbox" className="check" checked={o.isDefault} onChange={() => setGroup(g.key, (x) => toggleDefault(x, o.key))} />
                      <span className="t-caption">Pre-picked</span>
                    </label>
                    <span className="row cat-tools">
                      <button type="button" className="btn btn-quiet icon-btn cat-up" aria-label={`Move ${o.label || `option ${oi + 1}`} up`} disabled={oi === 0} onClick={() => setGroup(g.key, (x) => ({ ...x, options: move(x.options, oi, -1) }))}>
                        <Icon name="arrow" />
                      </button>
                      <button type="button" className="btn btn-quiet icon-btn cat-down" aria-label={`Move ${o.label || `option ${oi + 1}`} down`} disabled={oi === g.options.length - 1} onClick={() => setGroup(g.key, (x) => ({ ...x, options: move(x.options, oi, 1) }))}>
                        <Icon name="arrow" />
                      </button>
                      <button type="button" className="btn btn-quiet icon-btn" aria-label={`Remove ${o.label || `option ${oi + 1}`}`} onClick={() => setGroup(g.key, (x) => ({ ...x, options: x.options.filter((y) => y.key !== o.key) }))}>
                        <Icon name="close" />
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
              <button type="button" className="btn btn-quiet cat-add" disabled={g.options.length >= 20} onClick={() => setGroup(g.key, (x) => ({ ...x, options: [...x.options, newOption()] }))}>
                <Icon name="plus" />
                Add an option
              </button>
            </li>
          ))}
        </ol>
        <ListError name="groups" error={errors.groups} />
        <button type="button" className="btn cat-add" disabled={d.groups.length >= 10} onClick={() => update((x) => ({ ...x, groups: [...x.groups, newGroup()] }), 'groups')}>
          <Icon name="plus" />
          Add a choice group
        </button>
      </fieldset>

      <ComboBuilder d={d} errors={errors} update={update} shopId={shopId} selfId={selfId} />
    </section>
  );
}

function ComboBuilder({ d, errors, update, shopId, selfId }: { d: ProductDraft; errors: Errors; update: Update; shopId: string; selfId: string | null }) {
  const [q, setQ] = useState('');
  const [term, setTerm] = useState('');
  useEffect(() => {
    const t = window.setTimeout(() => setTerm(q.trim()), 250);
    return () => window.clearTimeout(t);
  }, [q]);
  const picker = useQuery({
    queryKey: catalogKeys.picker(shopId, term),
    queryFn: () => api.adminProducts(shopId, { q: term || undefined, status: 'active' }),
    enabled: !!shopId,
    staleTime: 30_000,
  });
  const candidates = comboCandidates(picker.data?.items ?? [], d.combo, selfId).slice(0, 8);

  return (
    <fieldset className="cat-fieldset">
      <legend className="t-label">Combo</legend>
      <p className="t-caption muted">Make this a meal of the shop’s products, like 2 BBQ sticks + rice + drink. Its stock comes from those items. Leave empty for a single item.</p>
      {d.combo.length ? (
        <ul className="stack s8">
          {d.combo.map((c) => (
            <li key={c.productId} className="cat-combo">
              <span className="grow t-label">{c.name}</span>
              <label className="sr-only" htmlFor={`combo-${c.productId}`}>
                Quantity of {c.name}
              </label>
              <div className="input-affix cat-num">
                <span aria-hidden="true">×</span>
                <input
                  id={`combo-${c.productId}`}
                  inputMode="numeric"
                  value={c.qty}
                  onChange={(e) => update((x) => ({ ...x, combo: x.combo.map((y) => (y.productId === c.productId ? { ...y, qty: e.target.value } : y)) }), 'combo')}
                />
              </div>
              <button type="button" className="btn btn-quiet icon-btn" aria-label={`Remove ${c.name} from the combo`} onClick={() => update((x) => ({ ...x, combo: x.combo.filter((y) => y.productId !== c.productId) }), 'combo')}>
                <Icon name="close" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <ListError name="combo" error={errors.combo} />
      <div className="stack s8">
        <label className="search cat-picker">
          <span className="sr-only">Find a product to add to the combo</span>
          <Icon name="search" />
          <input type="search" placeholder="Add an item: search this shop’s products" value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
        {picker.isError ? (
          <span className="t-caption muted">Couldn’t load products. Try again in a moment.</span>
        ) : candidates.length ? (
          <ul className="cat-picks" aria-label="Products to add">
            {candidates.map((p) => (
              <li key={p.id}>
                <button type="button" className="btn btn-quiet cat-pick" onClick={() => update((x) => ({ ...x, combo: addComboItem(x.combo, p) }), 'combo')}>
                  <Icon name="plus" size="sm" />
                  <span className="grow">{p.name}</span>
                  <span className="t-caption muted">{priceLabel(p)}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : picker.data ? (
          <span className="t-caption muted">{term ? 'No matching products.' : 'No other active products to add yet.'}</span>
        ) : null}
      </div>
    </fieldset>
  );
}
